import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { Helmet } from 'react-helmet-async';
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../components/Auth";
import { AppContext } from "../components/AppProvider";
import { httpsCallable } from "firebase/functions";
import { getAuth, sendEmailVerification } from "firebase/auth";
import { collection, query, where, and, or, getDocs, getDoc, doc, setDoc, addDoc, deleteDoc, GeoPoint, orderBy } from 'firebase/firestore';

import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Stack,
    Avatar,
        Divider,
        Rating,
        Dialog,
        DialogTitle,
        DialogContent,
        Tooltip,
    useTheme,
    useMediaQuery,
        IconButton,
} from '@mui/material';
import EmojiFoodBeverageIcon from '@mui/icons-material/EmojiFoodBeverage';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import InsightsIcon from '@mui/icons-material/Insights';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import DinnerDiningIcon from '@mui/icons-material/DinnerDining';
import LanguageIcon from '@mui/icons-material/Language';
import CloseIcon from '@mui/icons-material/Close';

const CoverPage = () => {
    const COLORS = useMemo(() => ({
        // Colorful & happy palette for a passionate look
        logoBackground: '#FFD1A3',
        camera: '#2E3434', // dark text
        coral: '#FF6B6B',
        orange: '#FF9F43',
        teal: '#00C2A8',
        purple: '#7B61FF',
        sunshine: '#FFD166',
        cream: '#FFF8F2',
        navy: '#14202B',
        gold: '#D4AF37'
    }), []);
    const { authUser, authInstance, firestoreInstance, functionsInstance, storageInstance } = useContext(AuthContext);
    const navigate = useNavigate();
    const { config } = useContext(AppContext) || {};

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));

    const [selectedIndex, setSelectedIndex] = useState(1);
    const [openImageDialog, setOpenImageDialog] = useState(false);

    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        if (!firestoreInstance) return;
        setLoading(true);
        // Any data fetching or initialization logic can go here

        setLoading(false);
    }, [firestoreInstance, functionsInstance, authUser, authInstance, storageInstance]);

    const thumbnails = useMemo(() => (isXs ? [1, 2, 3] : [1, 2, 3, 4, 5]), [isXs]);

    return (
        // colorful happy page: cream background with vibrant hero gradient
        <Box sx={{ bgcolor: COLORS.cream, minHeight: '100vh' }}>
            <Helmet>
                <title>MonadX — Direct Messaging Campaigns on X</title>
                <meta name="description" content="MonadX empowers creators to build, grow, and monetize their audience on X using automated Direct Message campaigns." />
                <meta property="og:title" content="MonadX" />
                <meta property="og:description" content="Automated, high-conversion DM campaigns for creators, founders, and marketers on X." />
            </Helmet>
            {/* Hero gradient band */}
            <Box sx={{ py: { xs: 6, md: 10 }, background: `linear-gradient(135deg, ${COLORS.coral}, ${COLORS.purple}, ${COLORS.sunshine})`, color: '#fff' }}>
                <Container maxWidth="lg">
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: { xs: 4, md: 4 } }}>
                    <Box sx={{ flex: '1 1 50%', p: { xs: 2, md: 0 } }}>
                    <Typography variant="h2" fontWeight={900} sx={{ mb: 1, fontSize: { xs: '2rem', md: '3rem' } }}>
                        MonadX
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 2, opacity: 0.95 }}>
                        The future of direct messaging campaigns on X
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.95, mb: 3 }}>
                        MonadX helps creators, founders, and marketers turn their X audience into a high-performing marketing channel using automated, high-conversion Direct Message (DM) campaigns.
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/sign-in')}
                        sx={{ bgcolor: '#ffffff', color: COLORS.coral, fontWeight: 700, '&:hover': { bgcolor: '#fff' } }}
                        >
                        Get Started
                        </Button>
                        <Button
                        variant="outlined"
                        size="large"
                        onClick={() => navigate('/about')}
                        sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
                        >
                        Explore Features
                        </Button>
                    </Stack>

                    <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ minWidth: 140, bgcolor: 'rgba(255,255,255,0.12)', p: 1.25, borderRadius: 1 }}>
                        <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>Creators</Typography>
                        <Typography variant="caption" sx={{ color: '#fff', opacity: 0.9 }}>Tools for audience growth</Typography>
                        </Box>
                        <Box sx={{ minWidth: 140, bgcolor: 'rgba(255,255,255,0.12)', p: 1.25, borderRadius: 1 }}>
                        <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>Automated DMs</Typography>
                        <Typography variant="caption" sx={{ color: '#fff', opacity: 0.9 }}>High-conversion sequences</Typography>
                        </Box>
                        <Box sx={{ minWidth: 140, bgcolor: 'rgba(255,255,255,0.12)', p: 1.25, borderRadius: 1 }}>
                        <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>Real-time Analytics</Typography>
                        <Typography variant="caption" sx={{ color: '#fff', opacity: 0.9 }}>Opens, clicks, conversions</Typography>
                        </Box>
                    </Box>
                    </Box>
                </Box>
                </Container>
            </Box>

            {/* Features list (centered vertical list) */}
            {/* How it works + Features */}
            <Container sx={{ py: { xs: 4, md: 8 } }}>
                <Typography variant="h4" align="center" fontWeight={800} sx={{ color: COLORS.navy, mb: 4 }}>
                How MonadX Works
                </Typography>

                <Grid container spacing={3} justifyContent="center" alignItems="stretch" sx={{ mb: 4 }}>
                {[{
                    id: 1,
                    icon: <CameraAltIcon />,
                    bg: COLORS.sunshine,
                    title: 'Capture Subscribers',
                    body: 'Collect opt-ins via tweet replies, likes, mentions, or custom links.'
                },{
                    id: 2,
                    icon: <TextFieldsIcon />,
                    bg: COLORS.coral,
                    title: 'Automated DMs',
                    body: 'Send one-off or sequenced DMs instantly or on a schedule.'
                },{
                    id: 3,
                    icon: <InsightsIcon />,
                    bg: COLORS.teal,
                    title: 'Real-time Analytics',
                    body: 'Track opens, clicks, and conversions to optimize campaigns.'
                },{
                    id: 4,
                    icon: <SaveAltIcon />,
                    bg: COLORS.purple,
                    title: 'Retargeting & Segments',
                    body: 'Retarget users based on behavior and build custom segments.'
                },{
                    id: 5,
                    icon: <LanguageIcon />,
                    bg: COLORS.orange,
                    title: 'Funnels & Triggers',
                    body: 'Build multi-step DM funnels with keyword triggers and branching.'
                }].map((s) => (
                    <Grid item xs={12} sm={6} md={6} key={s.id} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Card elevation={0} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, p: { xs: 1, md: 2 }, width: { xs: '100%', md: 360 }, height: { xs: 'auto', md: 160 }, border: '1px solid rgba(0,0,0,0.06)', borderRadius: 2, bgcolor: '#fff' }}>
                        <Box sx={{ width: 64, height: 64, borderRadius: 1.5, bgcolor: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {React.cloneElement(s.icon, { sx: { fontSize: 26, color: '#fff' } })}
                        </Box>

                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: { md: '120px' } }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: COLORS.navy }}>{s.title}</Typography>
                        <Typography variant="body2" sx={{ color: COLORS.navy, opacity: 0.85, mt: 0.5 }}>{s.body}</Typography>
                        </Box>
                    </Card>
                    </Grid>
                ))}
                </Grid>

                
            </Container>

            {/* Testimonials (centered list for alignment) */}
            <Container sx={{ py: { xs: 4, md: 8 } }}>
                <Typography variant="h5" align="center" fontWeight={800} sx={{ color: COLORS.navy, mb: 3 }}>
                Creators & Marketers ❤️
                </Typography>

                <Grid container justifyContent="center">
                <Grid item xs={12} md={8}>
                    <Box>
                    {[{
                        id: 1,
                        name: 'Maya T.',
                        text: 'MonadX grew our subscriber list from 2k to 18k in 60 days — DMs drove real sales and signups.',
                        rating: 5
                    },{
                        id: 2,
                        name: 'Ethan B.',
                        text: 'Automated DM funnels convert at rates much higher than our email campaigns — game changer for launches.',
                        rating: 5
                    },{
                        id: 3,
                        name: 'Priya R.',
                        text: 'Retargeting by clicks and behavior helped us convert warm leads into paying customers rapidly.',
                        rating: 4.5
                    },{
                        id: 4,
                        name: 'Daniel K.',
                        text: 'Simple OAuth setup, robust API integration, and usage-based billing scaled with our growth.',
                        rating: 5
                    }].map((t, idx) => (
                        <Box key={t.id} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', py: 2, borderBottom: idx < 3 ? '1px solid rgba(16,24,32,0.06)' : 'none' }}>
                        <Avatar sx={{ bgcolor: COLORS.sunshine, color: COLORS.navy }}>{t.name.split(' ')[0][0]}</Avatar>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: COLORS.navy }}>{t.name}</Typography>
                            <Rating value={t.rating} precision={0.5} readOnly size="small" sx={{ color: COLORS.coral }} />
                            <Typography variant="body2" sx={{ color: COLORS.navy, mt: 1 }}>{t.text}</Typography>
                        </Box>
                        </Box>
                    ))}
                    </Box>
                </Grid>
                </Grid>
            </Container>

            {/* FAQ + Bottom CTA */}
            <Container sx={{ py: { xs: 4, md: 8 } }}>
                <Grid container spacing={4} justifyContent="center">
                <Grid item xs={12} md={8}>
                    <Typography variant="h5" fontWeight={800} sx={{ color: COLORS.navy, mb: 2 }}>Frequently Asked</Typography>
                    <Box>
                    {[{
                        q: "How do followers opt in to receive DMs?",
                        a: "Followers opt in by replying to a tweet, liking, or clicking a custom opt-in link. MonadX records consent and only messages opted-in users."
                    },{
                        q: "Is MonadX compliant with X policies and privacy rules?",
                        a: "MonadX uses OAuth 2.0 and follows X platform rules. We provide unsubscribe tools, rate limits, and audit logs; you should also follow local regulations."
                    },{
                        q: "What integrations and features are available?",
                        a: "X API v2 (DMs, followers, tweet monitoring), real-time analytics, multi-step DM funnels, keyword triggers, and segments for retargeting."
                    },{
                        q: "How does pricing work?",
                        a: "We use a pay-per-use billing model that scales with message volume and feature usage. Contact sales for enterprise plans."
                    },{
                        q: "Can I segment and retarget users?",
                        a: "Yes — create segments based on behavior (clicked, didn't click, new subscribers) and retarget them with tailored DM sequences."
                    }].map((f, idx) => (
                        <Box key={idx} sx={{ py: 1.5, borderBottom: '1px solid rgba(16,24,32,0.04)' }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: COLORS.navy }}>{f.q}</Typography>
                        <Typography variant="body2" sx={{ color: COLORS.navy, opacity: 0.9 }}>{f.a}</Typography>
                        </Box>
                    ))}
                    </Box>
                </Grid>
                </Grid>
            </Container>
        </Box>
    )
}

export default CoverPage;